import { Component, Inject, Injectable, signal, WritableSignal } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { DateAdapter, MAT_DATE_FORMATS, MatNativeDateModule, MatOptionModule, NativeDateAdapter } from '@angular/material/core';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { FormControl } from '@angular/forms';
import { Observable, Subject, debounceTime, filter, map, startWith, switchMap, tap } from 'rxjs';
import { User, UserRole } from '../../../models/user';
import { UsersService } from '../../../services/user.service';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTabsModule } from '@angular/material/tabs';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { format } from 'date-fns';

export const MY_FORMATS = {
  parse: {
    dateInput: 'yyyy-MM-dd',
  },
  display: {
    dateInput: 'yyyy-MM-dd',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'yyyy-MM-dd',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

@Injectable()
export class CustomDateAdapter extends NativeDateAdapter {
  override format(date: Date, displayFormat: Object): string {
    return format(date, 'MMM dd, yyyy'); // Customize your format here
  }
}


@Component({
  selector: 'app-user-editor',
  standalone: true,
  templateUrl: './user-editor.component.html',
  styleUrls: ['./user-editor.component.scss'],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    { provide: DateAdapter, useClass: CustomDateAdapter },
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    MatTableModule,
    MatIconModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatOptionModule,
    MatSelectModule,
    MatCheckboxModule,
    MatTabsModule,
    DragDropModule,
    MatDatepickerModule,
    MatNativeDateModule
  ],
})
export class UserEditorComponent {
  title: string = "Edit User";
  user!: WritableSignal<User>;

  tagControl = new FormControl('');
  filteredTags$: Observable<string[]>;

  allTags: string[] = ['Admin', 'Finance', 'HR', 'IT', 'Root']; // Example tag list

  displayedColumns: string[] = ['name', 'association', 'tools'];
  groupDataSource = new MatTableDataSource([{ name: "testing" }, { name: "another test" }]);
  reportsToControl = new FormControl();
  _options = new Subject<User[]>();
  options = this._options.asObservable();
  selectedUser?: User;
  userRoles: UserRole[] = [
    { roleId: 0, userId: 0, role: { id: 0, roleName: "User", description: "" }},
    { roleId: 2, userId: 0, role: { id: 2, roleName: "Department Admin", description: "Can work within department's data." }},
    { roleId: 1, userId: 0, role: { id: 1, roleName: "System Admin", description: "Can do anything within the system." }}
  ];
  roleId: number = 0;

  constructor(
    public dialogRef: MatDialogRef<UserEditorComponent>,
    private userService: UsersService,
    @Inject(MAT_DIALOG_DATA) public data: { isNew: boolean, user: User }
  ) {
    this.title = this.data?.isNew ? "Add new user" : "Edit User";
    const user = structuredClone(this.data.user);
    this.user = signal<User>(user ?? <User><any>{ id: undefined, hawkId: '', email: '', displayName: '', groupMemberships: [] });
    this.roleId = user.userRoles[0]?.roleId;
    this.selectedUser = this.data.user.reportsTo;
    this.filteredTags$ = this.tagControl.valueChanges.pipe(
      startWith(''),
      map((value) => this._filterTags(value || ''))
    );
    this.reportsToControl.valueChanges.pipe(
      filter(c => c != null && typeof c === "string"),
      debounceTime(100),
      tap(v => console.log(v)),
      switchMap(c => this.userService.filterUsers([{ chip: null, value: c?.trim() }], 5, 0, "fullname", "asc"))
    ).subscribe(resp => this._options.next(resp.users));
  }

  private _filterTags(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.allTags.filter((tag) =>
      tag.toLowerCase().includes(filterValue)
    );
  }

  selectUser(event: MatAutocompleteSelectedEvent): void {
    console.debug(this.selectUser.name);
    // console.info(this.selectUser.name);
    this.selectedUser = event.option.value as User;
    this.reportsToControl.setValue('');
  }

  public displayReportsToWith(user: User) {
    return user?.hawkId;
  }

  setRoleId(event: MatSelectChange) {
    if (this.user().userRoles.length == 0) {
      this.user().userRoles = [ <any>{ userId: this.user().id } ]
    }

    this.roleId = this.user().userRoles[0].roleId = event.value;
  }

  save() {
    console.log(this.user());
    this.user().userRoles = this.user().userRoles.filter(ur => ur.roleId != 0);
    this.dialogRef.close({ user: this.user(), isNew: this.data.isNew, reportsTo: this.selectedUser });
  }

  cancel() {
    this.dialogRef.close();
  }
}
